import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Activity, Thermometer, HandMetal, Eye, Brain,
  FileEdit, Target, FileText, Dumbbell, Home,
  FileBarChart, ChevronDown, ChevronUp, Shield,
  ShoppingBag, Baby, CheckCircle,
  ClipboardList, LogOut, RefreshCw, Heart,
  ChevronsUp, ChevronsDown
} from 'lucide-react';
import PrimaryButton from '../../common/PrimaryButton';
import { useFeature } from '../../../hooks/useFeature';
import type { AssessmentFormHandle } from './NursingAssessmentForm';
import AdultTransitionToggle from './AdultTransitionToggle';
import { withPediatricFirstConfig, patientTypeLabel } from './assessmentFormConfig';
import ICDSelect from '../../common/ICDSelect';
import { joinIcd10Field, splitIcd10Field } from '../../../utils/icd10Entries';
import { StandardizedTestSection } from './StandardizedTestTable';
import { legacyEntries, pickTherapyTestFields, therapyTestErrors } from './standardizedTests';
import type { TherapyTestFieldsDto } from '../../../dtos/patientmr/assessments/therapy-tests.dto';

// Phase 19 F23: the old free-text test fields; legacy values show read-only as "Earlier entry"
const LEGACY_TEST_KEYS = [
  { key: 'Pediatric Development-Standardized Assessment Tool Used', label: 'Standardized Assessment Tool Used' },
  { key: 'Pediatric Development-Assessment Score/Percentile', label: 'Assessment Score/Percentile' },
];

// Assessment Type definitions
type AssessmentType = 'initial' | 'recertification' | 'discharge';

interface AssessmentTypeConfig {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface FieldConfig {
  name: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'checkbox-group' | 'number';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  showFor?: AssessmentType[];
  readonly?: boolean;
  min?: number;
  max?: number;
}

const ASSESSMENT_TYPES: Record<AssessmentType, AssessmentTypeConfig> = {
  initial: {
    label: 'Initial/Admission Evaluation (SOC)',
    description: 'Comprehensive OT evaluation for Start of Care. All baseline data collected.',
    icon: <ClipboardList size={16} />,
    color: 'blue'
  },
  recertification: {
    label: 'Recertification Evaluation',
    description: 'Periodic reassessment (60-90 days). Focus on progress and continued medical necessity.',
    icon: <RefreshCw size={16} />,
    color: 'amber'
  },
  discharge: {
    label: 'Discharge Evaluation',
    description: 'Final evaluation documenting outcomes, functional status, and recommendations.',
    icon: <LogOut size={16} />,
    color: 'green'
  }
};

interface OTAssessmentFormProps {
  editingId: string | null;
  patientId: string;
  onSave: (data: any) => void;
  existingData?: any;
  assessmentStatus?: string;
  isReadOnly?: boolean;
}

const OTAssessmentForm = forwardRef<AssessmentFormHandle, OTAssessmentFormProps>(
  ({ editingId, patientId, onSave, existingData = null, assessmentStatus, isReadOnly = false }, ref) => {
  // Phase 3: Medicare content (face-to-face, Medicare wording) is hidden unless the medicareContent flag is on
  const medicareEnabled = useFeature('medicareContent');
  // Default form configuration
  const defaultFormConfig = {
    assessmentType: 'initial' as AssessmentType,
    patientType: 'pediatric', // Phase 19 F26: 'pediatric' or 'adult' (transition, 18–21 toggle)
    settingType: 'home-health', // Phase 19 F26: always home health (no selector)
    showOptionalSections: false
  };

  // Helper to normalize assessment type from backend format to internal format
  const normalizeAssessmentType = (type: string): AssessmentType => {
    const typeMap: Record<string, AssessmentType> = {
      'START_OF_CARE': 'initial',
      'start_of_care': 'initial',
      'initial': 'initial',
      'REASSESSMENT': 'recertification',
      'reassessment': 'recertification',
      'recertification': 'recertification',
      'END_OF_CARE': 'discharge',
      'end_of_care': 'discharge',
      'discharge': 'discharge'
    };
    return typeMap[type] || 'initial';
  };

  // Form configuration state - MVP defaults to pediatric for home health
  const [formConfig, setFormConfig] = useState(() => {
    // If editing existing data with formConfig, use that immediately
    if (existingData && existingData.formConfig) {
      const config = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      config.assessmentType = normalizeAssessmentType(config.assessmentType);
      return config;
    }
    // Otherwise use defaults for new form
    return defaultFormConfig;
  });

  // Medical codes state
  const [secondaryICD10Codes, setSecondaryICD10Codes] = useState<string[]>([]);
  const [selectedSecondaryICD10, setSelectedSecondaryICD10] = useState<string>('');

  // Track if initial data has been loaded to prevent overwriting
  const initialLoadCompleteRef = useRef(false);

  // Collapsible sections state
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'Recertification Summary': true,
    'Discharge Summary': true,
    'Diagnosis and Coding': true,
    'Functional Limitations': true,
    'Home Health Certification': true,
    'Physician Orders and Certification': true,
    'Patient History': true,
    'Mental Status': true,
    'Pediatric Information': true,
    'Pediatric Development': true,
    'Sensory Processing': true,
    'Visual-Perceptual Assessment': true,
    'Cognitive Assessment': true,
    'Fine Motor Assessment': true,
    'Upper Extremity Assessment': true,
    'Pediatric PDN Occupational Therapy': true,
    'Home Environment Assessment': true,
    'Activities of Daily Living (ADLs)': true,
    'Treatment Plan': true,
    'Treatment Goals and Plan': true,
    'OT Attestation': true,
    'Standardized Tests': true
  });

  // Track if all sections are collapsed for toggle button
  const [, setAllSectionsCollapsed] = useState(true);

  useEffect(() => {
    // Dynamically check all currently rendered categories
    const allCategoryNames = getAssessmentCategories().map(cat => cat.name);
    const allCollapsed = allCategoryNames.every(
      (name) => collapsedSections[name] === true
    );
    setAllSectionsCollapsed(allCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getAssessmentCategories is recreated every render; the listed deps are the inputs that change the categories
  }, [collapsedSections, formConfig]);


  useEffect(() => {
    const handleToggleAll = (event) => {
      toggleAllSections(event.detail.collapse);
    };
    window.addEventListener('toggleAllSections', handleToggleAll);
    return () => window.removeEventListener('toggleAllSections', handleToggleAll);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toggleAllSections is recreated every render; the listener is re-registered when collapsedSections changes
  }, [collapsedSections]);



  // Helper to check if a field should be visible based on assessment type
  const shouldShowField = (field: FieldConfig): boolean => {
    if (!field.showFor) return true;
    return field.showFor.includes(formConfig.assessmentType);
  };

  // Helper to get dynamic placeholder based on assessment type
  const getPlaceholder = (field: FieldConfig): string => {
    const { assessmentType } = formConfig;
    
    if (assessmentType === 'recertification') {
      const recertPlaceholders: Record<string, string> = {
        'Chief Complaint': 'Current primary concerns or changes since last evaluation',
        'Medical History': 'Update on current condition and any changes since last evaluation',
        'Prior Level of Function': 'Document any changes to prior level of function since last evaluation',
        'Primary Functional Limitations': 'Current functional limitations - note any changes from baseline',
        'Treatment Goals': 'Updated goals for this certification period',
        'OT Frequency': 'e.g., Continue 2x/week OR Change to 1x/week',
        'Frequency Justification': 'Justify continued or changed frequency based on current patient status'
      };
      if (recertPlaceholders[field.name]) return recertPlaceholders[field.name];
    }
    
    if (assessmentType === 'discharge') {
      const dischargePlaceholders: Record<string, string> = {
        'Primary Functional Limitations': 'Functional status at discharge compared to admission',
        'Treatment Goals': 'Final goal status - Met/Partially Met/Not Met for each goal',
        'OT Summary': 'Summary of interventions provided and patient response during episode of care'
      };
      if (dischargePlaceholders[field.name]) return dischargePlaceholders[field.name];
    }
    
    return field.placeholder || `Enter ${field.name}...`;
  };

  // Get assessment categories based on configuration
  const getAssessmentCategories = () => {
    const categories = [];
    const { assessmentType } = formConfig;

    // ========== RECERTIFICATION SUMMARY (only for recert) ==========
    if (assessmentType === 'recertification') {
      categories.push({
        name: "Recertification Summary",
        priority: 0.5,
        required: true,
        description: "Document progress since last evaluation and justification for continued OT services.",
        fields: [
          { name: "Previous Evaluation Date", type: "date", required: true },
          { name: "Recertification Period Start", type: "date", required: true },
          { name: "Recertification Period End", type: "date", required: true },
          { name: "Changes Since Last Evaluation", type: "textarea", required: true,
            placeholder: "Document all changes in patient condition, functional status, equipment, or living situation since the last OT evaluation." },
          { name: "Progress Toward Goals", type: "textarea", required: true,
            placeholder: "For each treatment goal, document:\n- Goal description\n- Baseline measurement\n- Current measurement\n- Progress status (Met, Partially Met, Not Met, Ongoing)" },
          { name: "Continued Medical Necessity Justification", type: "textarea", required: true,
            placeholder: "Explain why continued skilled OT services are medically necessary. Include: ongoing functional deficits, skilled care requirements, safety concerns, and expected outcomes." },
          { name: "Barriers to Progress", type: "textarea",
            placeholder: "Document any barriers to achieving goals (medical complications, compliance issues, equipment delays, etc.)" },
          { name: "Plan Modifications", type: "textarea",
            placeholder: "Document any changes to treatment plan, goals, or frequency for the next certification period" },
          { name: "Physician Contact for Recertification", type: "select", required: true,
            options: ["Physician contacted - verbal orders obtained", "Physician contacted - awaiting response", "Face-to-face encounter completed", "Telehealth encounter completed", "Orders received via fax/portal"] },
          { name: "Physician Contact Date", type: "date" },
          { name: "Updated Orders Received", type: "select",
            options: ["No changes to orders", "Orders updated - see details", "Pending physician signature"] },
          { name: "Order Changes Description", type: "textarea", placeholder: "If orders were updated, describe the changes" }
        ]
      });
    }

    // ========== DISCHARGE SUMMARY (only for discharge) ==========
    if (assessmentType === 'discharge') {
      categories.push({
        name: "Discharge Summary",
        priority: 0.5,
        required: true,
        description: "Complete discharge documentation including outcomes, recommendations, and follow-up plans.",
        fields: [
          { name: "Discharge Date", type: "date", required: true },
          { name: "Original Start of Care Date", type: "date", required: true },
          { name: "Total Visits Provided", type: "text", placeholder: "e.g., 18 visits over 4 months" },
          { name: "Discharge Reason", type: "select", required: true,
            options: ["Goals met - OT no longer needed", "Goals partially met - maximum benefit achieved", "Patient/family request", "Moved out of service area", 
                      "Transferred to outpatient OT", "Transferred to another agency", "Hospitalization", "Medical decline - OT on hold",
                      "Non-compliance with treatment", "Physician discharged from OT", "Insurance/authorization ended", "Patient deceased", "Other - see notes"] },
          { name: "Discharge Reason Details", type: "textarea", placeholder: "Provide additional details about the discharge reason" },
          { name: "Goals Met Summary", type: "textarea", required: true,
            placeholder: "For each treatment goal, document final status:\n- Goal 1: [Met/Partially Met/Not Met] - Baseline: ___ Current: ___\n- Goal 2: [Met/Partially Met/Not Met] - Baseline: ___ Current: ___" },
          { name: "Functional Outcomes Summary", type: "textarea", required: true,
            placeholder: "Compare functional status at discharge to admission:\n- ADLs: Admission ___ → Discharge ___\n- Fine Motor: Admission ___ → Discharge ___\n- Sensory Processing: Admission ___ → Discharge ___" },
          { name: "Discharge ADL Status", type: "select", required: true,
            options: ["Independent in all ADLs", "Modified independent", "Supervision required", "Minimal assistance", "Moderate assistance", "Maximum assistance", "Dependent", "Declined from admission"] },
          { name: "Discharge Instructions Provided", type: "select", required: true,
            options: ["Yes - verbal and written HEP provided", "Yes - verbal instructions only", "Yes - written HEP only", 
                      "No - patient declined", "No - patient unable to receive", "N/A - patient deceased"] },
          { name: "Home Program Details", type: "textarea",
            placeholder: "List activities and exercises included in discharge home program" },
          { name: "Patient/Caregiver Verbalized Understanding", type: "select", required: true,
            options: ["Yes - demonstrated full understanding", "Yes - with some clarification needed", "Partial understanding - reinforced teaching", 
                      "Limited understanding - referred to physician", "Unable to assess", "N/A - patient deceased"] },
          { name: "Equipment at Discharge", type: "textarea",
            placeholder: "List adaptive equipment patient has at discharge (splints, adaptive utensils, dressing aids, etc.)" },
          { name: "Recommendations", type: "textarea", required: true,
            placeholder: "Recommendations for continued care:\n- Continue home program independently\n- Outpatient OT referral\n- Sensory diet continuation\n- Equipment maintenance" },
          { name: "Follow-up Appointments", type: "textarea", placeholder: "Scheduled follow-up appointments or referrals" },
          { name: "OT Discharge Summary", type: "textarea", required: true,
            placeholder: "Comprehensive narrative summary of the patient's OT episode: reason for referral, interventions provided, patient response, and current functional status." },
          { name: "Physician Notified of Discharge", type: "select", required: true,
            options: ["Yes - verbal notification", "Yes - written/fax notification", "Yes - via EMR/portal", "Pending notification", "N/A - physician ordered discharge"] },
          { name: "Physician Notification Date", type: "date" },
          { name: "Receiving Agency Name", type: "text",
            placeholder: "Name of agency/facility patient is transferring to (if applicable)" },
          { name: "Receiving Agency Contact", type: "text",
            placeholder: "Phone number or contact person at receiving agency" },
          { name: "Continuity of Care Notes", type: "textarea",
            placeholder: "Critical information for receiving provider: current care needs, equipment requirements, caregiver training status, pending issues" },
          { name: "Services Provided Summary", type: "textarea",
            placeholder: "Total visits/hours by discipline during episode" }
        ]
      });
    }

    // ========== DIAGNOSIS AND CODING (all types) ==========
    categories.push({
      name: "Diagnosis and Coding",
      priority: 1,
      required: true,
      fields: [
        { name: "Primary ICD-10 Code", type: "select", required: true },
        { name: "Secondary ICD-10 Codes", type: "custom-icd10-list" as any },
        { name: "Medical Necessity - Clinical Complexity", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Describe the patient's medical condition and complexity. Include:\n• Primary diagnosis and relevant comorbidities\n• Medical history affecting therapy needs\n• Why this condition requires skilled therapy (not routine/maintenance)\n• Technology dependence (ventilator, trach, feeding tube, etc.)" },
        { name: "Medical Necessity - Skilled Care Requirements", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Explain WHY skilled therapy is required:\n• Specific skilled techniques needed (not routine exercises)\n• Clinical judgment required for assessment/modification\n• Safety monitoring needed during treatment\n• Complex presentation requiring therapist expertise\n• Why caregiver or aide cannot provide this service" },
        { name: "Medical Necessity - Functional Impact", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Describe how impairments affect patient's function:\n• ADL limitations\n• Mobility/communication limitations\n• Safety risks without therapy\n• Impact on caregiver burden" },
        { name: "Medical Necessity - Expected Outcomes", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "What functional improvements are expected with therapy?\n• Specific measurable outcomes\n• Timeframe for anticipated progress\n• How therapy will reduce hospitalizations/complications" }
      ]
    });

    // ========== FUNCTIONAL LIMITATIONS (CMS-485 Section 8) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Functional Limitations",
        priority: 1.5,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "CMS-485 Section 8: Select all functional limitations that apply (at least one required for medical necessity)",
        fields: [
          { name: "Functional Limitation Categories", type: "checkbox-group", required: true,
            options: [
              "A - Amputation",
              "B - Bowel/Bladder (Incontinence)",
              "C - Contracture",
              "D - Hearing",
              "E - Paralysis",
              "F - Endurance",
              "G - Ambulation",
              "H - Speech",
              "I - Inadequate Nutrition",
              "J - Confusion",
              "K - Dyspnea",
              "L - Other (Specify)"
            ]
          },
          { name: "Other Functional Limitation Description", type: "textarea",
            placeholder: "If 'L - Other' selected above, describe the specific functional limitation" },
          { name: "Functional Limitation Impact", type: "textarea", required: true,
            placeholder: "Describe how these functional limitations affect patient's daily activities and safety. Explain why skilled OT is necessary to address these limitations." }
        ]
      });
    }

    // ========== HOME HEALTH CERTIFICATION (home health, initial and recert only) ==========
    if (formConfig.settingType === 'home-health' && assessmentType !== 'discharge') {
      categories.push({
        name: "Home Health Certification",
        priority: 2,
        required: true,
        description: assessmentType === 'recertification' 
          ? "Recertification of homebound status and continued OT need."
          : "Homebound status and OT service recommendations.",
        fields: [
          { name: "Homebound Status Criteria (Check all that apply)", type: "checkbox-group", required: true,
            options: [
              "Patient leaves home infrequently and with considerable and taxing effort",
              "Patient requires supportive devices",
              "Patient requires special transportation",
              "Patient requires assistance of another person",
              "Normal inability to leave home due to medical condition",
              "Physician has determined leaving home is medically contraindicated"
            ]
          },
          { name: "Supportive Devices Required", type: "text",
            placeholder: "Specify devices: walker, wheelchair, oxygen, ventilator, etc." },
          { name: "Special Transportation Details", type: "text",
            placeholder: "Describe transport needs: medical van, stretcher transport, ventilator-equipped vehicle" },
          { name: "Assistance Required", type: "textarea",
            placeholder: "Describe assistance needed: supervision, physical assist, continuous skilled nursing, cannot be left unattended" },
          { name: "Homebound Status Justification", type: "textarea", required: true,
            placeholder: "Document specific reasons patient meets homebound criteria. Include: ADL dependencies, safety concerns, cognitive limitations, medical equipment restrictions, or functional deficits preventing community access." },
          { name: "Referring Physician", type: "text", required: assessmentType === 'initial',
            showFor: ['initial'] as AssessmentType[],
            placeholder: "Physician name who referred/ordered OT services" },
          { name: "Referring Physician NPI", type: "text",
            showFor: ['initial'] as AssessmentType[],
            placeholder: "10-digit NPI number" },
          { name: "Start of Care Date", type: "date", required: true, showFor: ['initial'] as AssessmentType[] },
          { name: "Service Frequency - Visits Per Week", type: "number", required: true,
            placeholder: "e.g., 2", min: 1, max: 7 },
          { name: "Service Frequency - Minutes Per Visit", type: "select", required: true,
            options: ["30", "45", "60", "75", "90", "120"] },
          { name: "Service Frequency - Total Hours Per Week", type: "text", readonly: true,
            placeholder: "Auto-calculated from visits and minutes" },
          { name: "Service Duration - Start Date", type: "date", required: true },
          { name: "Service Duration - Anticipated End Date", type: "date", required: true },
          { name: "Service Duration - Total Days", type: "text", readonly: true,
            placeholder: "Auto-calculated from start and end dates" },
          { name: "Certification Period", type: "select", required: true,
            options: ["60 days", "90 days", "120 days", "180 days"] },
          { name: "Frequency Justification", type: "textarea", required: true, 
            placeholder: "Explain why this frequency is medically necessary based on patient's functional deficits and goals" }
        ]
      });
    }

    // ========== PHYSICIAN ORDERS AND CERTIFICATION (all types) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Physician Orders and Certification",
        priority: 2.5,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "Track physician order and signature status for CMS-485 compliance",
        fields: [
          { name: "Verbal Order Received", type: "select", required: true,
            options: ["Yes", "No"] },
          { name: "Verbal Order Date", type: "date" },
          { name: "Verbal Order Received By", type: "text" },
          { name: "Written POC Sent to Physician Date", type: "date", required: true },
          { name: "Method POC Sent to Physician", type: "select", required: true,
            options: ["Fax", "Secure Email", "Portal", "In-person", "Mail"] },
          { name: "Physician Received/Reviewed POC Date", type: "date" },
          { name: "Physician Signature Status", type: "select", required: true,
            options: ["Pending signature", "Signed - paper", "Signed - electronic", 
                      "Signed - fax", "Physician declined to sign", "Unable to obtain signature"] },
          { name: "Date Physician Signed POC", type: "date" },
          ...(medicareEnabled ? [
          { name: "Face-to-Face Encounter Required", type: "select",
            options: ["Yes - Medicare patient", "No - Medicaid/other payor"] },
          { name: "Face-to-Face Encounter Date", type: "date" },
          { name: "Face-to-Face Encounter Type", type: "select",
            options: ["In-person visit", "Telehealth (COVID-19 waiver)", "Hospital discharge visit"] },
          { name: "Face-to-Face Clinician Name", type: "text" }
          ] : [])
        ]
      });
    }

    // ========== PATIENT HISTORY (all types but abbreviated for recert/discharge) ==========
    categories.push({
      name: "Patient History",
      priority: 3,
      required: true,
      description: assessmentType === 'recertification' ? "Update patient history. Focus on changes since last evaluation." : undefined,
      fields: [
        { name: "Chief Complaint", type: "textarea", required: assessmentType !== 'discharge',
          placeholder: "Primary reason for OT referral" },
        { name: "History of Present Problem", type: "textarea", required: assessmentType === 'initial',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Onset, duration, and current impact of functional difficulties" },
        { name: "Past Medical History", type: "textarea", showFor: ['initial'] as AssessmentType[],
          placeholder: "Relevant medical history affecting OT treatment" },
        { name: "Surgical History", type: "textarea", showFor: ['initial'] as AssessmentType[],
          placeholder: "Relevant surgeries with approximate dates" },
        { name: "Prior Level of Function", type: "textarea", required: assessmentType === 'initial',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Functional status before current condition" },
        { name: "Current Medications", type: "textarea", 
          placeholder: "Medications affecting OT (pain meds, psychotropics, blood thinners, etc.)" },
        { name: "Precautions/Contraindications", type: "textarea", required: true,
          placeholder: "Weight bearing status, movement restrictions, behavioral precautions, sensory precautions, etc." }
      ]
    });

    // ========== MENTAL STATUS (CMS-485 Section 10) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Mental Status",
        priority: 3.8,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "CMS-485 Section 10: Patient's mental and cognitive status",
        fields: [
          { name: "Mental Status Indicators", type: "checkbox-group", required: true,
            options: ["Oriented", "Disoriented", "Forgetful", "Depressed", "Agitated", "Comatose"] },
          { name: "Orientation Level", type: "select", required: true,
            options: [
              "Oriented x4 (person, place, time, situation)",
              "Oriented x3 (person, place, time)",
              "Oriented x2",
              "Oriented x1",
              "Disoriented",
              "N/A - pediatric/unable to assess"
            ]
          },
          { name: "Mental Status Description", type: "textarea", required: true,
            placeholder: formConfig.patientType === 'pediatric'
              ? "For pediatric: Describe cognitive/developmental level, responsiveness to caregivers, ability to follow commands, communication method"
              : "Describe cognitive function, memory, attention, safety awareness, judgment as it relates to therapy" },
          { name: "Cognitive Level (Pediatric)", type: "text",
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., Functions at 18-24 month level" }
        ]
      });
    }

    // ========== PEDIATRIC INFORMATION (pediatric patients) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Pediatric Information",
        priority: 3.2,
        required: true,
        description: assessmentType === 'recertification' ? "Update pediatric information. Birth history fields are optional." : undefined,
        fields: [
          { name: "Chronological Age", type: "text", required: true, placeholder: "e.g., 3 years 6 months" },
          { name: "Adjusted/Corrected Age", type: "text", placeholder: "If premature, corrected age for developmental assessment" },
          { name: "Gestational Age at Birth", type: "text", showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 28 weeks" },
          { name: "Birth Weight", type: "text", showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 1100 grams" },
          { name: "Birth History", type: "textarea", showFor: ['initial'] as AssessmentType[],
            placeholder: "Include NICU stay, birth complications, early feeding/sensory issues" },
          { name: "Primary Caregiver", type: "text", required: assessmentType !== 'discharge', placeholder: "Name and relationship to patient" },
          { name: "Current School/Daycare", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Not applicable (infant/toddler)", "Daycare", "Preschool", "Elementary school", "Homebound", "Early intervention program", "Special education"] },
          { name: "IEP/IFSP Status", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["No IEP/IFSP", "IFSP in place (0-3 years)", "IEP in place (3+ years)", "Evaluation pending", "Not eligible"] },
          { name: "Other Therapies Receiving", type: "textarea", placeholder: "PT, Speech, ABA, etc. with frequency" }
        ]
      });
    }

    // ========== PEDIATRIC DEVELOPMENT (pediatric patients, all types) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Pediatric Development",
        priority: 3.5,
        required: true,
        description: assessmentType === 'discharge' ? "Final developmental status at discharge." : "Comprehensive developmental assessment for OT.",
        fields: [
          { name: "Fine Motor Development Level", type: "select", required: true,
            options: ["Age appropriate", "Mild delay (1-3 months behind)", "Moderate delay (3-6 months behind)", "Significant delay (6-12 months behind)", "Severe delay (>12 months behind)", "Unable to assess"] },
          { name: "Grasp Development", type: "select", required: true,
            options: ["Age-appropriate grasp patterns", "Emerging age-appropriate grasp", "Delayed grasp patterns", "Primitive grasp patterns only", "Unable to grasp", "Not yet expected"] },
          { name: "Grasp Details", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe grasp patterns observed: palmar, radial digital, tripod, pincer, etc." },
          { name: "Release Skills", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Controlled release", "Emerging controlled release", "Difficulty with release", "Unable to release voluntarily", "Not yet expected"] },
          { name: "Bilateral Coordination", type: "select", required: true,
            options: ["Age-appropriate bilateral skills", "Emerging bilateral coordination", "Difficulty with bilateral tasks", "Significant bilateral coordination deficits", "Unable to assess"] },
          { name: "Hand Dominance", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Right hand dominant", "Left hand dominant", "Mixed dominance (age-appropriate)", "Hand preference not yet established", "Hand preference should be established - not present"] },
          { name: "Visual Motor Integration", type: "select", required: true,
            options: ["Age-appropriate VMI", "Mild VMI difficulties", "Moderate VMI difficulties", "Significant VMI difficulties", "Unable to assess"] },
          { name: "Pre-Writing/Writing Skills", type: "select",
            options: ["Age-appropriate skills", "Emerging skills", "Delayed skills", "Significantly delayed", "Not yet expected", "N/A"] },
          { name: "Scissor Skills", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age-appropriate cutting", "Emerging cutting skills", "Snips only", "Unable to use scissors", "Not yet expected", "N/A - safety concern"] },
          { name: "Self-Care Development", type: "select", required: true,
            options: ["Age-appropriate self-care", "Mild delays in self-care", "Moderate delays in self-care", "Significant delays in self-care", "Dependent for self-care"] },
          { name: "Feeding Skills", type: "select", required: true,
            options: ["Age-appropriate feeding", "Finger feeding only", "Requires adapted utensils", "Requires hand-over-hand assist", "Tube fed - oral trials", "Tube fed - no oral intake", "NPO"] },
          { name: "Developmental Concerns Summary", type: "textarea", required: true,
            placeholder: "Summary of primary fine motor/developmental concerns and functional impact" }
        ]
      });
    }

    // ========== SENSORY PROCESSING (pediatric emphasis, all types) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Sensory Processing",
        priority: 3.7,
        required: true,
        description: assessmentType === 'discharge' ? "Final sensory processing status." : "Comprehensive sensory processing assessment.",
        fields: [
          { name: "Overall Sensory Processing", type: "select", required: true,
            options: ["Typical sensory processing", "Mild sensory processing differences", "Moderate sensory processing disorder", "Significant sensory processing disorder", "Unable to assess"] },
          { name: "Sensory Profile Tool Used", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Sensory Profile 2", "Infant/Toddler Sensory Profile", "Short Sensory Profile", "Sensory Processing Measure (SPM)", "Clinical observation only", "Not formally assessed"] },
          { name: "Tactile Processing", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Typical", "Over-responsive (tactile defensive)", "Under-responsive", "Seeking tactile input", "Mixed pattern"] },
          { name: "Tactile Details", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe tactile responses: textures, touch, grooming, clothing, messy play" },
          { name: "Vestibular Processing", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Typical", "Over-responsive (gravitational insecurity)", "Under-responsive", "Seeking movement", "Mixed pattern"] },
          { name: "Vestibular Details", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe vestibular responses: movement tolerance, playground equipment, car rides" },
          { name: "Proprioceptive Processing", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Typical", "Under-responsive (poor body awareness)", "Seeking proprioceptive input", "Difficulty grading force"] },
          { name: "Proprioceptive Details", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe: heavy work preferences, force modulation, body awareness" },
          { name: "Auditory Processing", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Typical", "Over-responsive", "Under-responsive", "Difficulty filtering sounds", "Mixed pattern"] },
          { name: "Visual Processing", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Typical", "Over-responsive", "Under-responsive", "Difficulty with visual attention", "Mixed pattern"] },
          { name: "Oral Sensory Processing", type: "select", required: true,
            options: ["Typical oral sensory", "Oral defensiveness", "Oral seeking", "Limited diet due to sensory", "Mixed oral sensory pattern"] },
          { name: "Oral Sensory Details", type: "textarea",
            placeholder: "Describe food textures accepted/avoided, oral seeking behaviors, mouthing" },
          { name: "Self-Regulation", type: "select", required: true,
            options: ["Age-appropriate self-regulation", "Mild self-regulation difficulties", "Moderate self-regulation difficulties", "Significant self-regulation difficulties"] },
          { name: "Sensory Diet Recommendations", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Recommended sensory strategies: heavy work, movement breaks, oral motor input, etc." }
        ]
      });
    }

    // ========== PEDIATRIC PDN OCCUPATIONAL THERAPY (pediatric + home health) ==========
    if (formConfig.patientType === 'pediatric' && formConfig.settingType === 'home-health') {
      categories.push({
        name: "Pediatric PDN Occupational Therapy",
        priority: 3.9,
        required: true,
        description: assessmentType === 'discharge' 
          ? "Final status for PDN OT considerations."
          : "Texas Medicaid PDN-specific OT documentation.",
        fields: [
          // Medical Complexity
          { name: "Tracheostomy Status", type: "select", required: true,
            options: assessmentType === 'discharge'
              ? ["No tracheostomy", "Tracheostomy present at discharge", "Tracheostomy decannulated during episode"]
              : ["No tracheostomy", "Tracheostomy present", "Recently decannulated"] },
          { name: "Ventilator Status", type: "select", required: true,
            options: assessmentType === 'discharge'
              ? ["No ventilator", "Ventilator dependent at discharge", "Weaned from ventilator during episode"]
              : ["No ventilator", "Ventilator dependent - continuous", "Ventilator dependent - nocturnal only", "Weaning in progress"] },
          { name: "Feeding Status", type: "select", required: true,
            options: ["Oral feeding only", "G-tube with oral trials", "G-tube only - no oral", "NG tube", "TPN", "Combination feeding"] },
          { name: "Medical Stability for OT", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Medically stable - full OT activities", "Stable with precautions - modified activities", "Unstable - OT on hold", "Unstable - bedside OT only"] },
          { name: "OT Precautions Related to Medical Status", type: "textarea",
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Positioning restrictions, activity tolerance, oxygen requirements, feeding precautions" },
          
          // Adaptive Equipment
          { name: "Current Adaptive Equipment", type: "textarea",
            placeholder: "List all OT-related equipment: splints, adapted utensils, seating, sensory equipment" },
          { name: "Equipment Fit and Condition", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["All equipment fits and functions properly", "Equipment needs adjustment", "Equipment needs replacement", "New equipment needed - see recommendations", "No OT equipment currently"] },
          { name: "Splinting Needs", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["No splints needed", "Current splints adequate", "Splint modification needed", "New splint fabrication needed", "Splint wear compliance issues"] },
          { name: "Splint Details", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Type of splints, wear schedule, purpose, any issues" },
          { name: "Seating/Positioning Equipment", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe positioning equipment and UE positioning needs" },
          
          // Feeding/Oral Motor (OT scope)
          { name: "Feeding Positioning", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Appropriate feeding position", "Positioning modifications needed", "Specialized seating for feeding", "Positioning significantly affects feeding"] },
          { name: "Self-Feeding Status", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age-appropriate self-feeding", "Emerging self-feeding", "Requires adapted equipment", "Requires physical assistance", "Unable to self-feed", "NPO - not applicable"] },
          { name: "Adapted Feeding Equipment", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Adapted utensils, cups, plates, positioning aids for feeding" },
          
          // Caregiver Training
          { name: "Caregiver OT Skill Level", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Excellent - independent with HEP and activities", "Good - needs occasional cueing", "Fair - needs regular supervision/training", "Poor - significant training needed", "Unable to participate in OT activities"] },
          { name: "Caregiver Training Provided", type: "textarea",
            placeholder: "Training provided: positioning, sensory strategies, feeding techniques, splint care, home program" },
          { name: "Caregiver Return Demonstration", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Successful demonstration of all skills", "Partial demonstration - needs practice", "Unable to demonstrate - reteach needed", "Not applicable"] },
          
          // OT in PDN Context
          { name: "OT Visit Frequency Requested", type: "text", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., 2x/week for 45 min sessions" },
          { name: "OT Frequency Justification", type: "textarea", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Justify OT frequency based on: developmental needs, medical complexity, caregiver training needs, equipment needs" },
          { name: "Coordination with PDN Staff", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "OT activities to be carried over by PDN nurses (positioning, sensory diet, feeding strategies, splint wear)" }
        ]
      });
    }

    // ========== UPPER EXTREMITY ASSESSMENT (initial and recert) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Upper Extremity Assessment",
        priority: 5,
        required: false,
        fields: [
          { name: "Dominant Hand", type: "select",
            options: ["Right", "Left", "Ambidextrous", "Not established (pediatric)"] },
          { name: "UE Range of Motion", type: "textarea",
            placeholder: "Document ROM limitations by joint (shoulder, elbow, wrist, hand)" },
          { name: "UE Strength", type: "textarea",
            placeholder: "Manual muscle testing or functional strength observations" },
          { name: "Grip Strength", type: "text", placeholder: "Dynamometer reading or functional grip assessment" },
          { name: "Pinch Strength", type: "text", placeholder: "Pinch gauge reading or functional pinch assessment" },
          { name: "Sensation", type: "select",
            options: ["Intact", "Diminished - light touch", "Diminished - protective", "Absent", "Hypersensitivity"] },
          { name: "Coordination", type: "select",
            options: ["Normal", "Mild incoordination", "Moderate incoordination", "Severe incoordination"] },
          { name: "Edema", type: "select",
            options: ["None", "Mild", "Moderate", "Severe"] },
          { name: "Pain", type: "select",
            options: ["No pain", "Mild (1-3/10)", "Moderate (4-6/10)", "Severe (7-10/10)"] },
          { name: "UE Summary", type: "textarea",
            placeholder: "Summary of upper extremity function and limitations" }
        ]
      });
    }

    // ========== FINE MOTOR SKILLS (all types) ==========
    categories.push({
      name: "Fine Motor Assessment",
      priority: 6,
      required: true,
      fields: [
        { name: "Overall Fine Motor Function", type: "select", required: true,
          options: formConfig.patientType === 'pediatric'
            ? ["Age-appropriate", "Mild impairment", "Moderate impairment", "Severe impairment", "Unable to assess"]
            : ["Functional", "Mild impairment", "Moderate impairment", "Severe impairment", "Non-functional"] },
        { name: "Dexterity", type: "select", required: true,
          options: ["Normal/Age-appropriate", "Mild impairment", "Moderate impairment", "Severe impairment"] },
        { name: "In-Hand Manipulation", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: formConfig.patientType === 'pediatric'
            ? ["Age-appropriate", "Emerging skills", "Delayed", "Significant difficulty", "Unable", "Not yet expected"]
            : ["Functional", "Mild difficulty", "Moderate difficulty", "Severe difficulty"] },
        { name: "Fine Motor Speed", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Normal speed", "Mildly slow", "Moderately slow", "Severely slow"] },
        { name: "Fine Motor Observations", type: "textarea",
          placeholder: "Specific observations about fine motor function" }
      ]
    });

    // ========== COGNITIVE ASSESSMENT (all types, expanded for adults) ==========
    categories.push({
      name: "Cognitive Assessment",
      priority: 7,
      required: formConfig.patientType !== 'pediatric',
      description: formConfig.patientType === 'pediatric' ? "Cognitive skills as they relate to functional performance." : undefined,
      fields: [
        { name: "Orientation", type: "select", required: formConfig.patientType !== 'pediatric',
          options: ["Oriented x4 (person, place, time, situation)", "Oriented x3", "Oriented x2", "Oriented x1", "Disoriented", "N/A - pediatric"] },
        { name: "Attention/Concentration", type: "select", required: true,
          options: formConfig.patientType === 'pediatric'
            ? ["Age-appropriate attention", "Mild attention difficulties", "Moderate attention difficulties", "Severe attention difficulties", "Unable to assess"]
            : ["Within normal limits", "Mildly impaired", "Moderately impaired", "Severely impaired"] },
        { name: "Memory", type: "select",
          options: formConfig.patientType === 'pediatric'
            ? ["Age-appropriate", "Mild difficulties", "Moderate difficulties", "Severe difficulties", "Unable to assess"]
            : ["Intact", "Mild impairment", "Moderate impairment", "Severe impairment"] },
        { name: "Problem Solving", type: "select",
          options: formConfig.patientType === 'pediatric'
            ? ["Age-appropriate", "Emerging skills", "Delayed", "Significant difficulty", "Unable to assess"]
            : ["Within normal limits", "Mildly impaired", "Moderately impaired", "Severely impaired"] },
        { name: "Safety Awareness", type: "select", required: true,
          options: ["Good safety awareness", "Fair - needs occasional reminders", "Poor - needs frequent supervision", "Impaired - constant supervision needed"] },
        { name: "Cognitive Notes", type: "textarea",
          placeholder: "Additional cognitive observations affecting function" }
      ]
    });

    // ========== ADL ASSESSMENT (all types) ==========
    categories.push({
      name: "Activities of Daily Living (ADLs)",
      priority: 8,
      required: true,
      description: assessmentType === 'discharge' ? "Compare to baseline/admission status." : undefined,
      fields: formConfig.patientType === 'pediatric' ? [
        { name: "Feeding/Eating", type: "select", required: true,
          options: ["Age-appropriate", "Emerging skills", "Requires adaptation", "Requires assistance", "Dependent", "Not age-appropriate to assess"] },
        { name: "Dressing", type: "select", required: true,
          options: ["Age-appropriate", "Emerging skills", "Requires adaptation", "Requires assistance", "Dependent", "Not age-appropriate to assess"] },
        { name: "Grooming/Hygiene", type: "select", required: true,
          options: ["Age-appropriate", "Emerging skills", "Requires adaptation", "Requires assistance", "Dependent", "Not age-appropriate to assess"] },
        { name: "Toileting", type: "select",
          options: ["Age-appropriate/Toilet trained", "Toilet training in progress", "Requires assistance", "Dependent/Not trained", "Not age-appropriate to assess"] },
        { name: "Bathing", type: "select",
          options: ["Age-appropriate participation", "Emerging participation", "Tolerates bathing", "Difficulty with bathing", "Not age-appropriate to assess"] },
        { name: "ADL Notes", type: "textarea",
          placeholder: "Additional observations about daily living skills" }
      ] : [
        { name: "Feeding/Eating", type: "select", required: true,
          options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent"] },
        { name: "Grooming", type: "select", required: true,
          options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent"] },
        { name: "Bathing", type: "select", required: true,
          options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent"] },
        { name: "Dressing Upper Body", type: "select", required: true,
          options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent"] },
        { name: "Dressing Lower Body", type: "select", required: true,
          options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent"] },
        { name: "Toileting", type: "select", required: true,
          options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent"] },
        { name: "Functional Mobility for ADLs", type: "select",
          options: ["Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent"] },
        { name: "ADL Notes", type: "textarea",
          placeholder: "Additional observations about daily living skills" }
      ]
    });

    // ========== IADLs (adult/geriatric, not discharge) ==========
    if (formConfig.patientType !== 'pediatric' && assessmentType !== 'discharge') {
      categories.push({
        name: "Instrumental Activities of Daily Living (IADLs)",
        priority: 9,
        required: false,
        fields: [
          { name: "Meal Preparation", type: "select",
            options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent", "Not Assessed"] },
          { name: "Medication Management", type: "select",
            options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent", "Not Assessed"] },
          { name: "Money Management", type: "select",
            options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent", "Not Assessed"] },
          { name: "Light Housekeeping", type: "select",
            options: ["Independent", "Modified Independent", "Supervision", "Minimal Assistance", "Moderate Assistance", "Maximum Assistance", "Dependent", "Not Assessed"] },
          { name: "IADL Notes", type: "textarea",
            placeholder: "Additional observations about instrumental activities" }
        ]
      });
    }

    // ========== HOME ENVIRONMENT (home health, not discharge) ==========
    if (formConfig.settingType === 'home-health' && assessmentType !== 'discharge') {
      categories.push({
        name: "Home Environment Assessment",
        priority: 10,
        required: assessmentType === 'initial',
        description: assessmentType === 'recertification' ? "Document any changes to home environment." : undefined,
        fields: [
          { name: "Home Safety Overall", type: "select", required: assessmentType === 'initial',
            options: ["Safe environment", "Minor hazards identified - addressed", "Minor hazards identified - pending", "Significant hazards - intervention needed"] },
          { name: "Bathroom Accessibility", type: "select",
            options: ["Accessible - no modifications needed", "Modifications in place", "Modifications needed", "Significant barriers"] },
          { name: "Kitchen Accessibility", type: "select",
            options: ["Accessible - no modifications needed", "Modifications in place", "Modifications needed", "Significant barriers", "N/A - does not use kitchen"] },
          { name: "Bedroom Setup", type: "select",
            options: ["Appropriate setup", "Modifications in place", "Modifications needed"] },
          { name: "Adaptive Equipment in Home", type: "textarea",
            placeholder: "Current adaptive equipment available (grab bars, shower bench, raised toilet, etc.)" },
          { name: "Equipment Recommendations", type: "textarea",
            placeholder: "Recommended adaptive equipment or home modifications" },
          { name: "Environment Notes", type: "textarea",
            placeholder: "Additional environmental observations" }
        ]
      });
    }

    // ========== TREATMENT PLAN (initial and recert) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Treatment Plan",
        priority: 11,
        required: true,
        fields: [
          { name: "Plan Created By", type: "text", required: true, readonly: true,
            placeholder: "Auto-populated from current user (e.g., Sarah Johnson, OTR/L)" },
          { name: "Plan Creation Date", type: "date", required: true, readonly: true,
            placeholder: "Auto-populated with today's date" },
          { name: "Short-Term Goals (60-90 days)", type: "textarea", required: true,
            placeholder: "List short-term goals (target 60-90 days from SOC). For each goal include:\n• Goal statement\n• Target date\n• Baseline measurement\n• Target measurement\n• How progress will be measured" },
          { name: "Long-Term Goals (120-180 days)", type: "textarea", required: true,
            placeholder: "List long-term goals (target 120-180 days from SOC). For each goal include:\n• Goal statement\n• Target date\n• Baseline measurement\n• Target measurement\n• How progress will be measured" },
          { name: "Planned Interventions", type: "textarea", required: true,
            placeholder: formConfig.patientType === 'pediatric'
              ? "Therapeutic activities, sensory integration, fine motor training, feeding therapy, ADL training, caregiver education, etc."
              : "Therapeutic activities, ADL training, fine motor exercises, cognitive retraining, home modifications, etc." },
          { name: "Home Program", type: "textarea",
            placeholder: "Activities to be performed between OT visits" },
          { name: "Rehabilitation Potential", type: "select", required: true,
            options: [
              "Excellent - expected to meet all goals",
              "Good - expected to meet most goals",
              "Fair - expected to make functional gains",
              "Guarded - limited expected progress",
              "Poor - maintenance/prevention focus"
            ]
          },
          { name: "Rehabilitation Potential Rationale", type: "textarea", required: true,
            placeholder: "Explain rationale for rehab potential based on patient's condition, cognitive status, caregiver support, compliance, medical stability" },
          { name: "Anticipated Discharge Plan", type: "textarea", required: true,
            showFor: ['initial'] as AssessmentType[],
            placeholder: "Describe anticipated discharge: expected to return to prior level of function, ongoing skilled care anticipated, specific discharge criteria, expected timeframe for discharge" },
          { name: "Frequency and Duration", type: "text", required: true,
            placeholder: "e.g., 2x/week for 8 weeks, then reassess" },
          { name: "Discharge Criteria", type: "textarea",
            placeholder: "Criteria for discharge from OT services" },
          { name: "Coordination of Care", type: "textarea",
            placeholder: "Coordination with nursing, PT, speech, physician, school, etc." }
        ]
      });
    }

    // ========== OT ATTESTATION (all types) ==========
    categories.push({
      name: "OT Attestation",
      priority: 12,
      required: true,
      description: assessmentType === 'discharge' ? "Final attestation for discharge evaluation." : undefined,
      fields: [
        { name: "OT Summary", type: "textarea", required: true,
          placeholder: assessmentType === 'discharge'
            ? "Summary of OT episode of care, patient progress, and discharge status."
            : "Summary of key findings, functional limitations, and OT plan." },
        { name: "Skilled OT Justification", type: "textarea", required: assessmentType !== 'discharge',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Justify why skilled OT services are required (complexity, safety, skilled techniques)" },
        { name: "OT Signature Attestation", type: "select", required: true,
          options: assessmentType === 'discharge'
            ? ["I attest this discharge evaluation is complete and accurate", "Evaluation pending additional information"]
            : ["I attest this evaluation is complete and accurate", "Evaluation pending additional information"] }
      ]
    });

    // ========== OPTIONAL SECTIONS ==========
    if (formConfig.showOptionalSections && assessmentType !== 'discharge') {
      categories.push({
        name: "Visual-Perceptual Assessment",
        priority: 13,
        required: false,
        fields: [
          { name: "Visual Acuity", type: "select",
            options: ["Normal/Corrected", "Mild impairment", "Moderate impairment", "Severe impairment", "Not assessed"] },
          { name: "Visual Fields", type: "select",
            options: ["Intact", "Right field cut", "Left field cut", "Bilateral field cuts", "Not assessed"] },
          { name: "Visual Tracking", type: "select",
            options: ["Normal", "Impaired", "Not assessed"] },
          { name: "Visual Perception", type: "select",
            options: ["Normal", "Mildly impaired", "Moderately impaired", "Severely impaired", "Not assessed"] },
          { name: "Visual-Motor Integration", type: "select",
            options: ["Normal", "Mildly impaired", "Moderately impaired", "Severely impaired", "Not assessed"] },
          { name: "Visual-Perceptual Notes", type: "textarea",
            placeholder: "Additional visual-perceptual observations" }
        ]
      });
    }

    return categories.sort((a, b) => a.priority - b.priority);
  };

  // Form data state management
  const [formData, setFormData] = useState(() => {
    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      // Load existing secondary ICD-10 codes if they exist
      const existingSecondaryICD10 = existingData.categories?.['Diagnosis and Coding-Secondary ICD-10 Codes'];
      if (existingSecondaryICD10 && typeof existingSecondaryICD10 === 'string') {
        const codes = splitIcd10Field(existingSecondaryICD10);
        setSecondaryICD10Codes(codes);
      }
      return existingData;
    }

    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        categories[`${category.name}-${field.name}`] = "";
      });
    });
    
    return {
      id: "",
      date: new Date().toISOString().split('T')[0],
      provider: "Current User, OTR/L",
      status: "Pending",
      category: "OT",
      lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }),
      categories: categories,
      formConfig: formConfig
    };
  });
  
  useEffect(() => {
    // Only run this effect once when existingData is first loaded
    // Skip if we've already loaded the initial data to prevent resetting user changes
    if (initialLoadCompleteRef.current) {
      return;
    }

    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      setFormData(existingData);
      // Merge existing formConfig with defaults to ensure all fields are present
      const mergedConfig = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      mergedConfig.assessmentType = normalizeAssessmentType(mergedConfig.assessmentType);
      setFormConfig(mergedConfig);
      // Mark initial load as complete after loading existing data
      initialLoadCompleteRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultFormConfig is a fresh object every render; only a new existingData should reload the form
  }, [existingData]);

  useEffect(() => {
    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        const fieldKey = `${category.name}-${field.name}`;
        categories[fieldKey] = formData.categories?.[fieldKey] || "";
      });
    });
    
    setFormData(prev => ({
      ...prev,
      categories: { ...prev.categories, ...categories },
      formConfig: formConfig
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only when the form type fields change; adding formConfig/formData would rebuild the categories on every edit
  }, [formConfig.assessmentType, formConfig.patientType, formConfig.settingType, formConfig.showOptionalSections]);

  // Auto-calculate total hours per week
  const serviceVisitsPerWeek = formData.categories?.['Home Health Certification-Service Frequency - Visits Per Week'];
  const serviceMinutesPerVisit = formData.categories?.['Home Health Certification-Service Frequency - Minutes Per Visit'];
  useEffect(() => {
    const visitsPerWeek = parseInt(serviceVisitsPerWeek || '0');
    const minutesPerVisit = parseInt(serviceMinutesPerVisit || '0');
    
    if (visitsPerWeek > 0 && minutesPerVisit > 0) {
      const totalHours = (visitsPerWeek * minutesPerVisit) / 60;
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Home Health Certification-Service Frequency - Total Hours Per Week': `${totalHours.toFixed(1)} hours`
        }
      }));
    }
  }, [serviceVisitsPerWeek, serviceMinutesPerVisit]);

  // Auto-calculate duration in days
  const serviceStartDate = formData.categories?.['Home Health Certification-Service Duration - Start Date'];
  const serviceEndDate = formData.categories?.['Home Health Certification-Service Duration - Anticipated End Date'];
  useEffect(() => {
    const startDate = serviceStartDate;
    const endDate = serviceEndDate;
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Home Health Certification-Service Duration - Total Days': `${days} days`
        }
      }));
    }
  }, [serviceStartDate, serviceEndDate]);

  // Auto-populate plan creator and date on form initialization
  useEffect(() => {
    if (!editingId && !formData.categories?.['Treatment Plan-Plan Created By']) {
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Treatment Plan-Plan Created By': formData.provider,
          'Treatment Plan-Plan Creation Date': new Date().toISOString().split('T')[0]
        }
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-fill only on open/provider change; adding the field would re-fill it after the user clears it
  }, [editingId, formData.provider]);

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      categories: { ...(prev.categories || {}), [fieldKey]: value }
    }));
  };

  const handleAddSecondaryICD10 = () => {
    if (selectedSecondaryICD10 && !secondaryICD10Codes.includes(selectedSecondaryICD10)) {
      const updatedCodes = [...secondaryICD10Codes, selectedSecondaryICD10];
      setSecondaryICD10Codes(updatedCodes);
      handleInputChange('Diagnosis and Coding-Secondary ICD-10 Codes', joinIcd10Field(updatedCodes));
      setSelectedSecondaryICD10('');
    }
  };

  const handleRemoveSecondaryICD10 = (codeToRemove: string) => {
    const updatedCodes = secondaryICD10Codes.filter(code => code !== codeToRemove);
    setSecondaryICD10Codes(updatedCodes);
    handleInputChange('Diagnosis and Coding-Secondary ICD-10 Codes', joinIcd10Field(updatedCodes));
  };

  const handleConfigChange = (key, value) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const toggleAllSections = (collapse) => {
    // Use all current category names for dynamic expand/collapse
    const allCategoryNames = getAssessmentCategories().map(cat => cat.name);
    const newState = { ...collapsedSections };
    allCategoryNames.forEach(key => {
      newState[key] = collapse;
    });
    setCollapsedSections(newState);
    setConfigSectionCollapsed(collapse); // Ensure evaluation config section is included
  };

  const getCategoryColorClasses = (categoryName) => {
    const colorMap = {
      "Recertification Summary": { bg: "bg-amber-50", border: "border-amber-200", hover: "hover:bg-amber-100" },
      "Discharge Summary": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Diagnosis and Coding": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Functional Limitations": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Mental Status": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Physician Orders and Certification": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Home Health Certification": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Patient History": { bg: "bg-gray-50", border: "border-gray-200", hover: "hover:bg-gray-100" },
      "Pediatric Information": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Pediatric Development": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Pediatric PDN Occupational Therapy": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Sensory Processing": { bg: "bg-orange-50", border: "border-orange-200", hover: "hover:bg-orange-100" },
      "Visual-Perceptual Assessment": { bg: "bg-indigo-50", border: "border-indigo-200", hover: "hover:bg-indigo-100" },
      "Cognitive Assessment": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Fine Motor Assessment": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Upper Extremity Assessment": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Activities of Daily Living (ADLs)": { bg: "bg-teal-50", border: "border-teal-200", hover: "hover:bg-teal-100" },
      "Instrumental Activities of Daily Living (IADLs)": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Home Environment Assessment": { bg: "bg-orange-50", border: "border-orange-200", hover: "hover:bg-orange-100" },
      "Treatment Plan": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "OT Attestation": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" }
    };
    return colorMap[categoryName] || { bg: "bg-white", border: "border-gray-200", hover: "hover:bg-gray-50" };
  };

  const getCategoryIcon = (categoryName) => {
    const icons = {
      "Recertification Summary": <RefreshCw size={18} className="mr-2 text-amber-600" />,
      "Discharge Summary": <LogOut size={18} className="mr-2 text-green-600" />,
      "Diagnosis and Coding": <FileBarChart size={18} className="mr-2 text-blue-600" />,
      "Home Health Certification": <Shield size={18} className="mr-2 text-green-600" />,
      "Patient History": <FileText size={18} className="mr-2 text-gray-700" />,
      "Pediatric Information": <Baby size={18} className="mr-2 text-purple-600" />,
      "Pediatric Development": <Activity size={18} className="mr-2 text-purple-600" />,
      "Sensory Processing": <Thermometer size={18} className="mr-2 text-orange-600" />,
      "Pediatric PDN Occupational Therapy": <Heart size={18} className="mr-2 text-red-600" />,
      "Upper Extremity Assessment": <Dumbbell size={18} className="mr-2 text-gray-700" />,
      "Fine Motor Assessment": <HandMetal size={18} className="mr-2 text-red-600" />,
      "Cognitive Assessment": <Brain size={18} className="mr-2 text-purple-700" />,
      "Activities of Daily Living (ADLs)": <Activity size={18} className="mr-2 text-green-700" />,
      "Instrumental Activities of Daily Living (IADLs)": <ShoppingBag size={18} className="mr-2 text-purple-700" />,
      "Home Environment Assessment": <Home size={18} className="mr-2 text-orange-600" />,
      "Treatment Plan": <Target size={18} className="mr-2 text-green-600" />,
      "OT Attestation": <CheckCircle size={18} className="mr-2 text-green-600" />,
      "Visual-Perceptual Assessment": <Eye size={18} className="mr-2 text-indigo-600" />,
      "Functional Limitations": <Activity size={18} className="mr-2 text-red-600" />,
      "Mental Status": <Brain size={18} className="mr-2 text-purple-600" />,
      "Physician Orders and Certification": <Shield size={18} className="mr-2 text-blue-600" />
 };
    return icons[categoryName] || <FileText size={18} className="mr-2 text-gray-700" />;
  };

  /**
   * CMS-485 COMPLIANT VALIDATION
   * Validates form data against CMS requirements for home health certification
   * 
   * Validation Rules:
   * 1. Required field validation (standard)
   * 2. Physician signature 14-day rule (CMS requirement)
   * 3. Medical necessity 500-character minimum (audit defense)
   * 4. Homebound criteria (at least one required)
   * 5. Functional limitations (at least one required)
   */
  const validateForm = () => {
    const errors = [];
    const categories = getAssessmentCategories();
    
    // 1. Basic required field validation
    categories.forEach(section => {
      section.fields.forEach(field => {
        if (shouldShowField(field) && field.required && !formData.categories?.[`${section.name}-${field.name}`]) {
          errors.push(`${field.name} is required`);
        }
      });
    });
    
    // ========== CMS-485 SPECIFIC VALIDATION RULES ==========
    
    // 2. Physician signature timing (14-day rule - CMS requirement)
    const verbalOrderDate = formData.categories?.['Physician Orders and Certification-Verbal Order Date'];
    const physicianReviewedDate = formData.categories?.['Physician Orders and Certification-Physician Received/Reviewed POC Date'];
    
    if (verbalOrderDate && physicianReviewedDate) {
      const daysDiff = Math.floor((new Date(physicianReviewedDate).getTime() - new Date(verbalOrderDate).getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff > 14) {
        errors.push('⚠️ WARNING: Physician must receive/review POC within 14 days of verbal order (CMS-485 requirement)');
      }
    }
    
    // 3. Medical necessity minimum length (500 characters - audit defense)
    const clinicalComplexity = formData.categories?.['Diagnosis and Coding-Medical Necessity - Clinical Complexity'] || '';
    const skilledCare = formData.categories?.['Diagnosis and Coding-Medical Necessity - Skilled Care Requirements'] || '';
    const functionalImpact = formData.categories?.['Diagnosis and Coding-Medical Necessity - Functional Impact'] || '';
    const expectedOutcomes = formData.categories?.['Diagnosis and Coding-Medical Necessity - Expected Outcomes'] || '';
    
    const totalMedNecLength = clinicalComplexity.length + skilledCare.length + functionalImpact.length + expectedOutcomes.length;
    
    if (totalMedNecLength < 500 && formConfig.assessmentType !== 'discharge') {
      errors.push('📋 Medical necessity documentation too brief - CMS audits require detailed justification (minimum 500 characters combined across all 4 sections)');
    }
    
    // 4. Homebound criteria (CMS-485 Section 15 - at least one required)
    const homeboundCriteria = formData.categories?.['Home Health Certification-Homebound Status Criteria (Check all that apply)'];
    if (formConfig.settingType === 'home-health' && formConfig.assessmentType !== 'discharge') {
      if (!homeboundCriteria || (Array.isArray(homeboundCriteria) ? homeboundCriteria.length === 0 : !homeboundCriteria)) {
        errors.push('🏠 Must select at least one homebound criterion (CMS-485 Section 15 requirement)');
      }
    }
    
    // 5. Functional limitations (CMS-485 Section 8 - at least one required)
    const funcLimitations = formData.categories?.['Functional Limitations-Functional Limitation Categories'];
    if (formConfig.assessmentType !== 'discharge') {
      if (!funcLimitations || (Array.isArray(funcLimitations) ? funcLimitations.length === 0 : !funcLimitations)) {
        errors.push('⚕️ Must select at least one functional limitation (CMS-485 Section 8 requirement)');
      }
    }
    
    // Phase 19 F23: test results or the reason none was given (initial / recertification)
    errors.push(...therapyTestErrors(formConfig.assessmentType, pickTherapyTestFields(formData)));

    return errors;
  };

  // Payload passed to onSave (Save, Submit and autosave all use it)
  const buildSaveData = () => ({
    ...formData,
    formConfig: withPediatricFirstConfig(formConfig),
    status: 'Draft',
    lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })
  });

  // Save keeps a DRAFT and never blocks on required fields; Submit runs validate() (Phase 4)
  const handleSave = () => {
    onSave(buildSaveData());
  };


  // Phase 19 F23: standardized tests are saved at the top level of data, next to categories
  const handleTherapyTestChange = (patch: Partial<TherapyTestFieldsDto>) => {
    setFormData(prev => ({ ...prev, ...patch }));
  };
  const showTests = formConfig.assessmentType !== 'discharge';
  const renderedCategories = getAssessmentCategories();
  // Shown just before the first section after the developmental section (priority 3.5)
  const testsAnchor = showTests ? renderedCategories.findIndex(c => c.priority > 3.5) : -1;
  const testsSection = showTests ? (
    <StandardizedTestSection
      discipline="OT"
      value={pickTherapyTestFields(formData)}
      onChange={handleTherapyTestChange}
      readOnly={isReadOnly}
      legacy={legacyEntries(formData.categories, LEGACY_TEST_KEYS)}
      collapsed={collapsedSections['Standardized Tests'] === true}
      onToggle={() => toggleSection('Standardized Tests')}
    />
  ) : null;

  useImperativeHandle(ref, () => ({
    triggerSave: () => handleSave(),
    validate: () => validateForm(),
    getSaveData: () => buildSaveData(),
    getSnapshot: () => JSON.stringify(formData),
  }));

  return (
    <div className="space-y-6">
      <div className="bg-white text-left">

        {/* Header */}
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggleAllSections(!Object.values(collapsedSections).every(v => v))}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors"
            >
              {Object.values(collapsedSections).every(v => v) ? (
                <>
                  <ChevronsDown size={16} />
                  <span>Expand All</span>
                </>
              ) : (
                <>
                  <ChevronsUp size={16} />
                  <span>Collapse All</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Configuration Section - Collapsible */}
        <div className="bg-gray-50 rounded-lg mb-6 border border-gray-200">
          <div
            className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
            onClick={() => setConfigSectionCollapsed(!configSectionCollapsed)}
          >
            <div className="flex items-center gap-3">
              <FileEdit size={18} className="text-blue-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-700">Evaluation Configuration</h4>
                {configSectionCollapsed && formConfig.assessmentType && ASSESSMENT_TYPES[formConfig.assessmentType] && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {ASSESSMENT_TYPES[formConfig.assessmentType].label} • {patientTypeLabel(formConfig.patientType)}
                  </p>
                )}
              </div>
            </div>
            <div>
              {configSectionCollapsed ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
            </div>
          </div>

          <div className={configSectionCollapsed ? 'hidden' : 'p-4 pt-0'}>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Evaluation Type</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {(Object.keys(ASSESSMENT_TYPES) as AssessmentType[]).map((type) => {
                const config = ASSESSMENT_TYPES[type];
                const isSelected = formConfig.assessmentType === type;
                const colorClasses = {
                  blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                  amber: isSelected ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                  green: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                };
                const textColorClasses = {
                  blue: isSelected ? 'text-blue-600' : 'text-gray-500',
                  amber: isSelected ? 'text-amber-600' : 'text-gray-500',
                  green: isSelected ? 'text-green-600' : 'text-gray-500'
                };
                
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => (!editingId || assessmentStatus === 'DRAFT') && !isReadOnly && handleConfigChange('assessmentType', type)}
                    disabled={(!!editingId && assessmentStatus !== 'DRAFT') || isReadOnly}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${colorClasses[config.color]} ${editingId && assessmentStatus !== 'DRAFT' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center mb-1">
                      <span className={`mr-2 ${textColorClasses[config.color]}`}>{config.icon}</span>
                      <span className={`font-medium text-sm ${isSelected ? `text-${config.color}-900` : 'text-gray-700'}`}>
                        {config.label}
                      </span>
                    </div>
                    <p className={`text-xs ${isSelected ? `text-${config.color}-700` : 'text-gray-500'}`}>
                      {config.description}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Assessment Type Notice */}
            {formConfig.assessmentType === 'recertification' && (
              <div className="p-3 rounded-lg flex items-start mb-4 bg-amber-50 border border-amber-200">
                <RefreshCw size={18} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <strong>Recertification Mode:</strong> Birth history and baseline fields are hidden. 
                  Focus on documenting progress toward goals and justifying continued OT services.
                </div>
              </div>
            )}
            
            {formConfig.assessmentType === 'discharge' && (
              <div className="p-3 rounded-lg flex items-start mb-4 bg-green-50 border border-green-200">
                <LogOut size={18} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <strong>Discharge Mode:</strong> Treatment planning fields are hidden. 
                  Focus on documenting outcomes, goal achievement, and recommendations.
                </div>
              </div>
            )}

            {/* Configuration Fields */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                <input type="date" className="w-full p-2 border border-gray-300 rounded text-base"
                  disabled={isReadOnly}
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})} />
              </div>
              <AdultTransitionToggle disabled={isReadOnly} patientType={formConfig.patientType}
                onChange={(patientType) => handleConfigChange('patientType', patientType)} />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
                <input type="text" className="w-full p-2 border border-gray-300 rounded text-base"
                  disabled={isReadOnly}
                  value={formData.provider}
                  onChange={(e) => setFormData({...formData, provider: e.target.value})} />
              </div>
            </div>
            
            {/* Optional sections toggle */}
            <div className="mt-4">
              <label className="flex items-center text-sm text-gray-600">
                <input type="checkbox" disabled={isReadOnly} className="mr-2" checked={formConfig.showOptionalSections}
                  onChange={(e) => handleConfigChange('showOptionalSections', e.target.checked)} />
                Show Optional Sections (Visual-Perceptual)
              </label>
            </div>
            
            {/* Pediatric PDN Notice */}
            {formConfig.patientType === 'pediatric' && formConfig.settingType === 'home-health' && (
              <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start">
                <Baby size={18} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-purple-800">
                  <strong>Pediatric Home Health OT:</strong> Includes specialized developmental, sensory processing, and PDN-specific documentation for Texas Medicaid.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main form content - categories */}
        {renderedCategories.map((category, categoryIndex) => {
          const visibleFields = category.fields.filter(field => shouldShowField(field));
          if (visibleFields.length === 0) return categoryIndex === testsAnchor ? <div key={categoryIndex}>{testsSection}</div> : null;
          
          const colors = getCategoryColorClasses(category.name);
          
          return (
            <div key={categoryIndex} className="mb-6">
              {categoryIndex === testsAnchor && testsSection}
              <div className={`rounded-lg border ${colors.border} shadow-sm ${colors.bg}`}>
                <div className={`flex items-center justify-between cursor-pointer p-4 ${colors.hover} rounded-t-lg transition-colors`} onClick={() => toggleSection(category.name)}>
                  <div className="flex items-center">
                    {getCategoryIcon(category.name)}
                    <h4 className="text-base font-medium text-gray-900">{category.name}</h4>
                    {category.required && <span className="ml-2 text-xs text-red-600 font-medium">Required</span>}
                  </div>
                  <div>
                    {collapsedSections[category.name] ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
                  </div>
                </div>
                
                {category.description && !collapsedSections[category.name] && (
                  <p className="text-sm text-gray-600 px-4 pb-2 border-b border-gray-100">{category.description}</p>
                )}

                <div className={`p-4 ${collapsedSections[category.name] ? 'hidden' : 'block'}`}>
                {visibleFields.map((field, fieldIndex) => {
                  const fieldKey = `${category.name}-${field.name}`;
                  const placeholder = getPlaceholder(field);
                  
                  return (
                    <div key={fieldIndex} className="mb-4 min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        {field.name}
                        {field.required && <span className="ml-1 text-red-600">*</span>}
                      </label>
                      
                      {field.type === 'select' && field.name === 'Primary ICD-10 Code' && (
                        <div>
                          <ICDSelect
                            value={formData.categories?.[fieldKey] || ''}
                            onChange={(val) => handleInputChange(fieldKey, val)}
                            placeholder={`Select ${field.name}...`}
                            className={`min-w-0 ${field.required && !formData.categories?.[fieldKey] ? 'ring-1 ring-red-300' : ''}`}
                            disabled={isReadOnly}
                          />
                        </div>
                      )}
                      {field.type === 'select' && field.name !== 'Primary ICD-10 Code' && (
                        <select
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                        >
                          <option value="">Select {field.name}...</option>
                          {field.options?.map((option, idx) => <option key={idx} value={option}>{option}</option>)}
                        </select>
                      )}
                      
                      {field.type === 'date' && (
                        <input type="date"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)} />
                      )}
                      
                      {field.type === 'textarea' && (
                        <textarea rows={3}
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder} />
                      )}
                      
                      {field.type === 'text' && (
                        <input type="text"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder}
                          readOnly={field.readonly} />
                      )}

                      {field.type === 'number' && (
                        <input type="number"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder}
                          readOnly={field.readonly}
                          min={field.min}
                          max={field.max} />
                      )}

                      {field.type === 'checkbox-group' && (
                        <div className="space-y-2">
                          {field.options?.map((option, idx) => {
                            const currentValues = formData.categories?.[fieldKey] ? 
                              (Array.isArray(formData.categories[fieldKey]) ? formData.categories[fieldKey] : formData.categories[fieldKey].split(',')) : [];
                            return (
                              <label key={idx} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300"
                                  checked={currentValues.includes(option)}
                                  onChange={(e) => {
                                    const newValues = e.target.checked
                                      ? [...currentValues, option]
                                      : currentValues.filter(v => v !== option);
                                    handleInputChange(fieldKey, newValues);
                                  }}
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {field.type === 'custom-icd10-list' && (
                        <div className="space-y-3">
                          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                            <ICDSelect
                              exclude={secondaryICD10Codes}
                              value={selectedSecondaryICD10}
                              onChange={(val) => setSelectedSecondaryICD10(val)}
                              placeholder="Select ICD-10 code..."
                              className="flex-1 w-full"
                              disabled={isReadOnly}
                            />
                            <PrimaryButton
                              type="button"
                              onClick={handleAddSecondaryICD10}
                              disabled={!selectedSecondaryICD10 || isReadOnly}
                              className="whitespace-nowrap w-full sm:w-auto"
                            >
                              Add ICD Code
                            </PrimaryButton>
                          </div>
                          {secondaryICD10Codes.length > 0 && (
                            <div className="border border-gray-200 rounded p-3 bg-gray-50">
                              <p className="text-sm font-medium text-gray-700 mb-2">Added Secondary Diagnoses:</p>
                              <ul className="space-y-2">
                                {secondaryICD10Codes.map((code, idx) => (
                                  <li key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                    <span className="text-sm">{code}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveSecondaryICD10(code)}
                                      disabled={isReadOnly}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          );
        })}
        {testsAnchor === -1 && testsSection}
        
      </div>
    </div>
  );
  }
);
OTAssessmentForm.displayName = 'OTAssessmentForm';

export default OTAssessmentForm;
